import { richTextToHTML } from '../../../../../clients/uniform-client/enhancers/utils';
import {
	ContentfulFeature,
	FeatureData,
	FeatureAdditionalInformation,
	RelevantPlan,
} from '../types.ts';

import type { Document } from '@contentful/rich-text-types';

// --- Minimal local types matching the GQL response shape ---

interface RelevantPlanGql {
	planTitleExternal: string;
}

interface RelevantPlanCollectionGql {
	items: RelevantPlanGql[];
}

interface FeatureAdditionalInformationGql {
	featureAdditionalInformation: string;
	relevantPlanCollection: RelevantPlanCollectionGql;
}

interface FeatureAdditionalInformationCollectionGql {
	items: FeatureAdditionalInformationGql[];
}

interface FeatureItemGql {
	callOut: string | null;
	featureNameExternal: string;
	description: { json: Document } | null;
	relevantPlanCollection: RelevantPlanCollectionGql;
	relevantUrl: string | null;
	featureAdditionalInformationCollection: FeatureAdditionalInformationCollectionGql;
}

interface FeaturesCollectionGql {
	items: FeatureItemGql[];
}

interface CompareFeatureGroupGql {
	featureGroupTitleExternal: string;
	featuresCollection: FeaturesCollectionGql;
}

interface CompareFeaturesCollectionGql {
	items: CompareFeatureGroupGql[];
}

interface ComparePlanGql {
	planTitleExternal: string;
}

interface ComparePlansCollectionGql {
	items: ComparePlanGql[];
}

interface PricingCollectionItemGql {
	comparePlansCollection: ComparePlansCollectionGql;
	compareFeaturesCollection: CompareFeaturesCollectionGql;
}

export interface PricingPageAdditionalContentData {
	pricingCollection: {
		items: PricingCollectionItemGql[];
	};
}

export interface MappedTableData {
	planTitles: string[];
	features: ContentfulFeature[];
}

// --- Mapping ---

const mapFeatureItem = (feature: FeatureItemGql): FeatureData => ({
	fields: {
		featureNameExternal: feature.featureNameExternal,
		description:
			typeof feature.description?.json === 'object' &&
			feature.description.json?.nodeType === 'document'
				? richTextToHTML(feature.description.json)
				: undefined,
		relevantUrl: feature.relevantUrl ?? undefined,
		callOut: feature.callOut ?? undefined,
		relevantPlan: feature.relevantPlanCollection.items.map((plan): RelevantPlan => ({
			fields: { planTitleExternal: plan.planTitleExternal },
		})),
		featureAdditionalInformation: feature.featureAdditionalInformationCollection.items.map(
			(info): FeatureAdditionalInformation => ({
				fields: {
					featureAdditionalInformation: info.featureAdditionalInformation,
					relevantPlan: info.relevantPlanCollection.items.map((plan): RelevantPlan => ({
						fields: {
							planTitleExternal: plan.planTitleExternal,
						},
					})),
				},
			}),
		),
	},
});

const mapCompareFeatureGroup = (group: CompareFeatureGroupGql): ContentfulFeature => ({
	fields: {
		featureGroupTitleExternal: group.featureGroupTitleExternal,
		features: group.featuresCollection.items.map(mapFeatureItem),
	},
});

export const mapAdditionalContentToTableData = (
	data: PricingPageAdditionalContentData,
): MappedTableData => {
	const item = data.pricingCollection.items[0];
	if (!item) {
		return { planTitles: [], features: [] };
	}
	return {
		planTitles: item.comparePlansCollection.items.map((plan) => plan.planTitleExternal),
		features: item.compareFeaturesCollection.items.map(mapCompareFeatureGroup),
	};
};
